import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import { useUser } from "../contexts/UserContext";
import { useTheme } from "@mui/material/styles";
import Sidebar from "../components/Sidebar";
import TourProvider from "../components/tour/TourProvider";
import HelpRow from "../components/tour/HelpRow";
import SubscriptionBanner from "../components/subscription/SubscriptionBanner";
import PageTransition from "../components/PageTransition";
import TopBar, { Crumb } from "../components/TopBar";
import { PageIconContext } from "../components/PageHeader";
import HomeIcon from "@mui/icons-material/Home";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import GroupIcon from "@mui/icons-material/Group";
import CommunityIcon from "@mui/icons-material/Groups";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssessmentIcon from "@mui/icons-material/Assessment";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import StraightenIcon from "@mui/icons-material/Straighten";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CampaignIcon from "@mui/icons-material/Campaign";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import { getAllBusinesses } from "../services/VenueService";
import { PaymentOutlined } from "@mui/icons-material";
import { COLORS } from "../assets/theme";
import {getMyProfile, MyProfile} from "../services/UsersService";

import ReplayIcon from "@mui/icons-material/Replay";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ProductionQuantityLimitsOutlinedIcon from '@mui/icons-material/ProductionQuantityLimitsOutlined';
import SportsIcon from "@mui/icons-material/Sports";
import SportsHandballIcon from "@mui/icons-material/SportsHandball";
import GroupsIcon from "@mui/icons-material/Groups";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import { PERMISSIONS } from "../utils/permissions";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CategoryIcon from "@mui/icons-material/Category";
import { IS_CLINIC, LABELS, SHOW } from "../platform/config";
interface UserProfile {
  fullName: string;
  userName: string;
  email: string;
  password: string;
  address: string;
  phone: string;
  banner?: string | null;
}

const SIDEBAR_COLLAPSED_KEY = "backoffice.sidebarCollapsed";

// Pages reachable from inside other pages rather than the sidebar; matched
// against the path after /dashboard/:businessId.
const EXTRA_CRUMBS: [RegExp, (basePath: string, rest: string) => Crumb[]][] = [
  [/^(overview|settings)$/, () => [{ label: "Dashboard" }]],
  [/^staff$/, () => [{ label: IS_CLINIC ? "Doctors & Staff" : "Staff" }]],
  [
    /^invoicepdf\//,
    (base) => [
      { label: "Finance" },
      { label: "Invoices", to: `${base}/finance/invoices` },
      { label: "Invoice" },
    ],
  ],
  [
    /^clients\/details\//,
    (base) => [
      { label: LABELS.clients },
      { label: `${LABELS.clients} List`, to: `${base}/clients/clients` },
      { label: `${LABELS.client} Details` },
    ],
  ],
  [/^clients\/info$/, () => [{ label: LABELS.clients }, { label: `${LABELS.clients} Info` }]],
  [
    /^suppliers\/details\//,
    (base) => [
      { label: "Inventory" },
      { label: "Purchases", to: `${base}/purchases` },
      { label: "Supplier Details" },
    ],
  ],
  [/^reports\/balance$/, () => [{ label: "Finance" }, { label: "Balance" }]],
  [/^reports\/notes$/, () => [{ label: "Finance" }, { label: "Notes" }]],
  [
    /^settings\/(specialities|currencies|units)$/,
    (_base, rest) => [
      { label: "Settings" },
      { label: rest.replace(/^settings\//, "").replace(/^./, (c) => c.toUpperCase()) },
    ],
  ],
];

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { user } = useUser();
 const [profile, setProfile] = useState<MyProfile | undefined>(undefined);
 
useEffect(() => {
  const fetchProfile = async () => {
    try {
      const res = await getMyProfile();
      setProfile(res);
    } catch (err) {
      console.error("Failed to load profile", err);
    }
  };

  fetchProfile();
}, []);

// Listen to user context changes and update profile accordingly
useEffect(() => {
  if (user) {
    setProfile((prev) => ({
      ...prev,
      ...user,
      fullName: user.fullName || prev?.fullName || "",
      email: user.email || prev?.email || "",
      phone: user.phone || prev?.phone || "",
      country: user.country || prev?.country || "",
      banner: user.banner !== undefined ? user.banner : (prev?.banner || null),
    } as MyProfile));
  }
}, [user]);

  const noNavRoutes = ["/login", "/signup", "/forgot-password"];
  const showNavBar = !noNavRoutes.includes(location.pathname);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [venueName, setVenueName] = useState("");
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawerWidth = 220;
  const collapsedWidth = 70;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (saved !== null) return saved === "1";
    } catch {
      // Storage unavailable — fall through to the screen-size default.
    }
    // No saved choice yet: start as an icon rail on tablets, where the full
    // 220px sidebar squeezes the calendar.
    return typeof window !== "undefined" && window.innerWidth < 1024;
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? "1" : "0");
    } catch {
      // Storage unavailable (private mode etc.) — the choice just won't persist.
    }
  }, [sidebarCollapsed]);
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const { businessId } = useParams<{ businessId: string }>();
  const basePath = businessId ? `/dashboard/${businessId}` : "/dashboard";
  const shouldRedirect = !businessId || businessId === "clients";

  useEffect(() => {
    if (shouldRedirect) {
      navigate("/");
    }
  }, [shouldRedirect, navigate]);
  const menuItems = [
    { label: "Dashboard", icon: <HomeIcon />, to: `${basePath}` },
    {
      label: "Appointment",
      icon: <EventAvailableIcon />,
      to: `${basePath}/appointment`,
    },
  
    {
      label: LABELS.clients,
      icon: <PeopleIcon />,
      children: [
        {
          label: `${LABELS.clients} List`,
          icon: <GroupIcon />,
          to: `${basePath}/clients/clients`,
        },
        {
          label: "Community",
          icon: <CommunityIcon />,
          to: `${basePath}/clients/community`,
        },
      ],
    },
    // A clinic's doctors are its staff; the sports build reaches Staff from
    // inside other pages instead.
    ...(IS_CLINIC && SHOW.staffPage
      ? [
          {
            label: "Doctors & Staff",
            icon: <MedicalServicesIcon />,
            to: `${basePath}/staff`,
          },
        ]
      : []),
    ...(SHOW.matchmaking
      ? [
          {
            label: "Matchmaking",
            icon: <SportsTennisIcon />,
            to: `${basePath}/matchmaking`,
            permission: PERMISSIONS.MANAGE_MATCHMAKING,
          },
        ]
      : []),
    {
      label: "Classes",
      icon: <SportsIcon />,
      feature: "CLASSES",
      children: [
        {
          label: "Coaches",
          icon: <SportsHandballIcon />,
          to: `${basePath}/classes/coaches`,
        },
        {
          label: "Classes",
          icon: <GroupsIcon />,
          to: `${basePath}/classes/classes`,
        },
        {
          label: "Packages",
          icon: <CardMembershipIcon />,
          to: `${basePath}/classes/packages`,
        },
        {
          label: "Client Packages",
          icon: <PeopleIcon />,
          to: `${basePath}/classes/client-packages`,
        },
      ],
    },
    {
      label: "Finance",
      icon: <PaymentOutlined />,
      children: [
        {
          label: "Invoices",
          icon: <ReceiptIcon />,
          to: `${basePath}/finance/invoices`,
        },
        // {
        //   label: "Quotations",
        //   icon: <RequestQuoteIcon />,
        //   to: `${basePath}/finance/quotations`,
        // },
        {
          label: "Expenses",
          icon: <AccountBalanceWalletIcon />,
          to: `${basePath}/finance/expenses`,
        },
        {
          label: "Reports",
          icon: <AssessmentIcon />,
          to: `${basePath}/finance/reports`,
        },
        {
          label: "Refunds",
          icon: <ReplayIcon />,
          to: `${basePath}/finance/refunds`,
        },
        {
          label: "Revenue",
          icon: <TrendingUpIcon />,
          to: `${basePath}/reports/revenue`,
        },
        {
          label: "Payments",
          icon: <PaymentsIcon />,
          to: `${basePath}/reports/payments`,
        },
        // {
        //   label: "Notes",
        //   icon: <ReceiptLongIcon />,
        //   to: `${basePath}/reports/notes`,
        // },
         {
          label: "PnL",
          icon: <AccountBalanceIcon />,
          to: `${basePath}/reports/pnl`,
        },
        //  {
        //   label: "Balance",
        //   icon: <AccountBalanceIcon />,
        //   to: `${basePath}/reports/balance`,
        // },
      ],
    },
    {
      label: "Inventory",
      icon: <ProductionQuantityLimitsOutlinedIcon />,
      feature: "STOCK",
      children: [
        {
          label: "Stocks",
          icon: <WarehouseIcon />,
          to: `${basePath}/stock`,
        },
        {
          label: "Purchases",
          icon: <ShoppingBasketIcon />,
          to: `${basePath}/purchases`,
        },
        {
          label: "Products",
          icon: <ShoppingBagIcon />,
          to: `${basePath}/products`,
        },
      ],
    },
      {
      label: "User Notifications",
      icon: <NotificationsIcon />,
      to: `${basePath}/userNotifications`,
    },
    {
      label: "Banners",
      icon: <CampaignIcon />,
      to: `${basePath}/banners`,
    },
    {
      label: "PromoCode",
      icon: <LocalOfferIcon />,
      to: `${basePath}/promocode`,
    },
    {
      label: "Loyalty",
      icon: <CardGiftcardIcon />,
      feature: "LOYALTY",
      children: [
        {
          label: "Settings",
          icon: <TuneIcon />,
          to: `${basePath}/loyalty/settings`,
        },
        {
          label: "Clients Loyalty",
          icon: <PeopleIcon />,
          to: `${basePath}/loyalty/clients`,
        },
      ],
    },
    {
      label: "Settings",
      icon: <SettingsIcon />,
      children: [
        ...(SHOW.specialities
          ? [
              {
                label: "Specialities",
                icon: <CategoryIcon />,
                to: `${basePath}/settings/specialities`,
              },
            ]
          : []),
        {
          label: "Services",
          icon: <MiscellaneousServicesIcon />,
          to: `${basePath}/settings/services`,
        },
       /* {
          label: "Currencies",
          icon: <CurrencyExchangeIcon />,
          to: `${basePath}/settings/currencies`,
        },*/
       /* {
          label: "Units",
          icon: <StraightenIcon />,
          to: `${basePath}/settings/units`,
        },*/
        {
          label: `${LABELS.clientFile} settings`,
          icon: <DrawOutlinedIcon />,
          to: `${basePath}/settings/consent-forms`,
        },
        {
          label: "Notification center",
          icon: <NotificationsActiveOutlinedIcon />,
          to: `${basePath}/settings/notification-center`,
        },
        {
          label: "Advanced",
          icon: <TuneIcon />,
          to: `${basePath}/settings/advanced`,
        },
      ],
    },
  ];

  useEffect(() => {
    const fetchVenueName = async () => {
      if (!businessId || shouldRedirect) return;

      try {
        const venues = await getAllBusinesses();
        const venue = venues.find((v: any) => v.id === businessId);
        if (venue) {
          setVenueName(venue.name || "");
        }
      } catch (error) {
        console.error("Error fetching venue name:", error);
      }
    };

    fetchVenueName();
  }, [businessId, shouldRedirect]);

  const crumbs: Crumb[] = (() => {
    const path = location.pathname.replace(/\/+$/, "");
    const trail: Crumb[] = venueName ? [{ label: venueName, to: basePath }] : [];
    for (const item of menuItems) {
      if (item.to === path) return [...trail, { label: item.label }];
      for (const child of item.children ?? []) {
        if (child.to === path) {
          return [...trail, { label: item.label }, { label: child.label }];
        }
      }
    }
    const rest = path.slice(basePath.length).replace(/^\//, "");
    const extra = EXTRA_CRUMBS.find(([pattern]) => pattern.test(rest));
    return extra ? [...trail, ...extra[1](basePath, rest)] : trail;
  })();
  const pageTitle = crumbs.length ? crumbs[crumbs.length - 1].label : "";
  // Icon for the page's title: the matching sidebar entry, or for pages
  // outside the menu (e.g. client details) the section they belong to.
  const pageIcon: React.ReactNode = (() => {
    const path = location.pathname.replace(/\/+$/, "");
    for (const item of menuItems) {
      if (item.to === path) return item.icon;
      const child = item.children?.find((c) => c.to === path);
      if (child) return child.icon;
    }
    const section = crumbs.find((c) => c.label !== venueName)?.label;
    return menuItems.find((item) => item.label === section)?.icon ?? null;
  })();

  useEffect(() => {
    if (!pageTitle) return;
    const previous = document.title;
    document.title =
      venueName && pageTitle !== venueName
        ? `${pageTitle} · ${venueName}`
        : pageTitle;
    return () => {
      document.title = previous;
    };
  }, [pageTitle, venueName]);

  if (shouldRedirect) {
    return null;
  }

  return (
    <TourProvider base={basePath}>
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: COLORS.MainBackground,
        overflowX: "hidden",
      }}
    >
      {showNavBar && (
        <TopBar
          profile={profile}
          onMenuClick={handleDrawerToggle}
          crumbs={crumbs}
        />
      )}
      <Sidebar
        mobileOpen={mobileOpen}
        onDrawerToggle={handleDrawerToggle}
        sidebarCollapsed={sidebarCollapsed}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        drawerWidth={drawerWidth}
        collapsedWidth={collapsedWidth}
        menuItems={menuItems}
        venueName={venueName}
        footer={(collapsed) => <HelpRow compact={collapsed} />}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pr: { xs: 2, sm: 4 },
          pl: { xs: 2, sm: 3 },
          mt: { xs: 7, sm: 8 },
          boxSizing: "border-box",
          width: isSmUp
            ? `calc(100% - ${
                sidebarCollapsed ? collapsedWidth : drawerWidth
              }px)`
            : "100%",
          transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.short,
          }),
          maxWidth: "100%",
          overflowX: "hidden",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          "& > *": { minWidth: 0 },
        }}
      >
        <SubscriptionBanner sx={{ mt: 2 }} />
        <PageIconContext.Provider value={pageIcon}>
          <PageTransition>
            <Outlet />
          </PageTransition>
        </PageIconContext.Provider>
      </Box>
    </Box>
    </TourProvider>
  );
};

export default DashboardLayout;
